import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createDocumentType,
  deleteDocumentType,
  getAllDocumentTypes,
  getDocumentTypeById,
  toggleDocumentType,
  updateDocumentType,
} from "../../services/api/api";

/**
 * Hook para obter todos os tipos de documentos
 * @param {boolean} activeOnly - Se true, retorna apenas tipos ativos
 * @returns {object} - { data, isLoading, error, refetch }
 */
export const useDocumentTypes = (activeOnly = false) => {
  return useQuery(
    ["documentTypes", activeOnly],
    () => getAllDocumentTypes(activeOnly),
    {
      staleTime: 1000 * 60 * 10, // 10 minutos
      retry: 1,
    }
  );
};

/**
 * Hook para buscar tipo de documento específico
 * @param {number} id - ID do tipo de documento
 * @param {boolean} enabled - Se a query deve ser executada
 * @returns {object} - { data, isLoading, error }
 */
export const useDocumentTypeById = (id, enabled = true) => {
  return useQuery(
    ["documentType", id],
    () => getDocumentTypeById(id),
    {
      enabled: !!id && enabled,
      staleTime: Infinity,
      retry: 1,
    }
  );
};

/**
 * Hook para criar tipo de documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useCreateDocumentType = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data) => createDocumentType(data),
    {
      onSuccess: () => {
        // Invalida cache de tipos de documentos
        queryClient.invalidateQueries(["documentTypes"]);
      },
    }
  );
};

/**
 * Hook para atualizar tipo de documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useUpdateDocumentType = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }) => updateDocumentType(id, data),
    {
      onSuccess: (data, variables) => {
        // Atualiza cache do tipo específico
        queryClient.invalidateQueries(["documentType", variables.id]);
        // Atualiza lista geral
        queryClient.invalidateQueries(["documentTypes"]);
      },
    }
  );
};

/**
 * Hook para ativar/desativar tipo de documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useToggleDocumentType = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, isActive }) => toggleDocumentType(id, isActive),
    {
      onSuccess: (data, variables) => {
        // Atualiza cache do tipo específico
        queryClient.invalidateQueries(["documentType", variables.id]);
        // Atualiza lista geral
        queryClient.invalidateQueries(["documentTypes"]);
      },
    }
  );
};

/**
 * Hook para deletar tipo de documento
 * @returns {object} - { mutate, mutateAsync, isLoading, error }
 */
export const useDeleteDocumentType = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteDocumentType(id),
    {
      onSuccess: () => {
        // Invalida cache de tipos de documentos
        queryClient.invalidateQueries(["documentTypes"]);
      },
    }
  );
};

